/** Copyright © 2024 Qcells. All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized use,
 * duplication, or distribution of software is strictly prohibited.
 */
import isEmpty from 'lodash-es/isEmpty';
import uniq from 'lodash-es/uniq';
import { SORT_ORDER } from '@hems/util/src/constant/constant';
import { ERROR_LEVEL, ERROR_PREFIX, ERROR_STATUS_TYPE, ERROR_RESOLVED_FILTER, ERROR_TYPE, } from '@hems/util/src/constant/eventHistory';
import { defaultPeriod, defaultStartDateTimeOptions, extractDateWithoutOffset, getDateWithAdjustedTime, getLocalDate, } from '@hems/util/src/helper/dateHelper';
import { addSortOrderMark, isEmptyArray, isNull } from '@hems/util/src/helper/helper';
import { getStringValue, isErrorLevelType } from '@hems/util/src/helper/tsguardHelper';
const dateFormatConfig = { isTime: true, needUtcConvert: true };
export const getErrorHistoryParams = (condition) => {
    const { errorType, pageNumber, pageSize, sort, search, date = defaultPeriod, errorLevel, excludeErrorCodes, errorResolved, } = condition;
    const commonParams = {
        errorType,
        pageNumber,
        pageSize,
        sort: getStringValue(sort),
        search: getStringValue(search),
        cleared: errorResolved === ERROR_RESOLVED_FILTER.RESOLVED,
        errorLevel: errorLevel?.toUpperCase(),
        excludeErrorCodes: getStringValue(excludeErrorCodes),
    };
    if (errorResolved === ERROR_RESOLVED_FILTER.CURRENT) {
        return commonParams;
    }
    return {
        ...commonParams,
        clearDateStart: getDateWithAdjustedTime(date?.start, defaultStartDateTimeOptions).toISOString(),
    };
};
const getDeviceId = (deviceId, subDeviceId) => {
    if (isNull(subDeviceId)) {
        return deviceId;
    }
    return `${deviceId}(${subDeviceId})`;
};
const errorLevelMap = {
    [ERROR_LEVEL.FAULT]: 'device.fault',
    [ERROR_LEVEL.WARNING]: 'device.warning',
    [ERROR_LEVEL.ALARM]: 'common.alarm',
};
const getErrorLevel = (errorLevel, t) => isErrorLevelType(errorLevel) ? t(errorLevelMap[errorLevel]) : errorLevel;
const getDescription = (display) => {
    return getStringValue(display.description) ?? '';
};
export const getErrorData = (errorList, totalCount, t) => {
    return {
        data: errorList.map((errorInfo) => ({
            ...errorInfo,
            deviceId: getDeviceId(errorInfo.deviceId, errorInfo.subDeviceId),
            triggerDate: errorInfo.triggerDate
                ? getLocalDate(extractDateWithoutOffset(errorInfo.triggerDate), dateFormatConfig)
                : '',
            clearDate: errorInfo.clearDate
                ? getLocalDate(extractDateWithoutOffset(errorInfo.clearDate), dateFormatConfig)
                : '',
            triggerDateUtc: errorInfo.triggerDate ?? null,
            clearDateUtc: errorInfo.clearDate ?? null,
            errorLevel: getErrorLevel(errorInfo.errorLevel, t),
            description: getDescription(errorInfo.display),
        })),
        totalCount,
    };
};
export const getRecentErrorData = (errorList, t) => {
    return errorList.map((error) => ({
        siteId: error.siteId,
        deviceId: getDeviceId(error.deviceId, error.subDeviceId),
        errorLevel: getErrorLevel(error.errorLevel, t),
        triggerDate: getLocalDate(extractDateWithoutOffset(error.triggerDate), dateFormatConfig),
        errorCode: error.errorCode,
        // TODO: 기존의 recent event history에서 서버에서 주는 값이 항상 'Y'인지 확인 필요
        newFlag: 'Y',
    }));
};
export const getSiteErrorParams = (siteId, errorType) => {
    return {
        sort: addSortOrderMark('triggerDate', SORT_ORDER.DESCENDING),
        errorType,
        cleared: false,
        search: siteId,
    };
};
const getErrorInfoByPrefix = (errors, prefix) => {
    return errors.filter((error) => error.errorCode.startsWith(prefix));
};
export const getSiteErrorStatusType = (errors) => {
    const faults = errors.some((error) => error.errorLevel === ERROR_LEVEL.FAULT);
    const warnings = errors.some((error) => error.errorLevel === ERROR_LEVEL.WARNING);
    if (faults) {
        return ERROR_STATUS_TYPE.ERROR;
    }
    if (warnings) {
        return ERROR_STATUS_TYPE.WARNING;
    }
    return null;
};
export const getSiteErrorData = (errorList) => {
    const errorCodeAndDate = errorList.map((error) => ({
        ...error,
        triggerDate: getLocalDate(extractDateWithoutOffset(error.triggerDate), dateFormatConfig),
        triggerDateUtc: error.triggerDate ?? null,
    }));
    const siteErrors = {};
    Object.values(ERROR_PREFIX).forEach((prefix) => {
        const errorsByPrefix = getErrorInfoByPrefix(errorCodeAndDate, prefix);
        siteErrors[prefix] = { errorStatusType: getSiteErrorStatusType(errorsByPrefix), errorInfo: errorsByPrefix };
    });
    return siteErrors;
};
const getDeviceIdFilter = (errors, deviceId) => {
    let errorInfos = errors?.errorInfo;
    if (deviceId) {
        errorInfos = errors?.errorInfo?.filter((error) => error.deviceId === deviceId);
    }
    return {
        errorStatusType: getSiteErrorStatusType(errorInfos ?? []),
        errorInfo: errorInfos ?? [],
    };
};
export const getErrorCodeAndDateForAC = (errors, deviceId) => {
    if (!errors || isEmpty(errors.errorInfo)) {
        return [];
    }
    const errorCodeAndDate = uniq(getDeviceIdFilter(errors, deviceId).errorInfo?.map((error) => ({
        errorCode: error.errorCode,
        triggerDate: error.triggerDate,
    })));
    return errorCodeAndDate;
};
export const getErrorCodeAndDateForACFromArray = (errorArray, deviceId) => {
    const isErrorCodesEmpty = errorArray.every((item) => item?.errorInfo?.length === 0);
    if (isErrorCodesEmpty) {
        return [];
    }
    const errors = errorArray
        .map((item) => getErrorCodeAndDateForAC(item, deviceId))
        .filter((item) => !isEmptyArray(item));
    return errors.flat();
};
export const getErrorStatusTypeFromArray = (errorArray, deviceId) => {
    const isErrorCodesEmpty = errorArray.every((item) => item?.errorInfo?.length === 0);
    if (isErrorCodesEmpty) {
        return;
    }
    const errorStatusTypeByArray = errorArray.map((item) => getDeviceIdFilter(item, deviceId).errorStatusType);
    if (errorStatusTypeByArray.includes(ERROR_STATUS_TYPE.ERROR)) {
        return ERROR_STATUS_TYPE.ERROR;
    }
    if (errorStatusTypeByArray.includes(ERROR_STATUS_TYPE.WARNING)) {
        return ERROR_STATUS_TYPE.WARNING;
    }
    return;
};
export const getAllErrorCodeList = (errorCodeInfoList, errorType) => {
    if (errorType === ERROR_TYPE.MAJOR) {
        return uniq(errorCodeInfoList.map((errorCode) => `${errorCode.major}`)).sort();
    }
    return errorCodeInfoList
        .map((errorCode) => `${errorCode.major}${errorCode.minor}${errorCode.level.charAt(0)}`)
        .sort();
};
